import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Upload, CheckCircle2, FileText } from "lucide-react";
import { submitApplication, apiError } from "../../lib/api";

const FIELD =
  "w-full border border-border bg-secondary px-4 py-3 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-brand";

export default function ApplicationForm({ job = null, source = "careers", onDone, tid = "app" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "", location: "", experience: "", message: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [answers, setAnswers] = useState({});
  const [ack, setAck] = useState(false);
  const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const questions = job?.screening_questions || [];
  const ackText = job?.acknowledgment_text || "I understand this position is contingent upon contract award and does not constitute an offer of employment.";

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) return toast.error("Resume must be a PDF, DOC, or DOCX file.");
    if (f.size > 10 * 1024 * 1024) return toast.error("Resume must be under 10MB.");
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Please add your name and email.");
    if (!job && !form.role) return toast.error("Please add your target role or discipline.");
    if (!file) return toast.error("Please attach your resume.");
    for (const q of questions) {
      if (q.required && !String(answers[q.id] || "").trim()) return toast.error(`Please answer: ${q.question}`);
    }
    if (job?.acknowledgment_required && !ack) return toast.error("Please accept the acknowledgment to continue.");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("role", job ? job.title : form.role);
      fd.append("location", form.location);
      fd.append("experience", form.experience);
      fd.append("message", form.message);
      fd.append("jobId", job?.id || "");
      fd.append("jobTitle", job?.title || "");
      fd.append("source", source);
      fd.append("screeningAnswers", JSON.stringify(questions.map((q) => ({
        question_id: q.id, question: q.question, type: q.type, required: q.required, disqualifying: q.disqualifying, answer: answers[q.id] || "",
      }))));
      fd.append("acknowledgment", ack ? "true" : "false");
      fd.append("resume", file);
      await submitApplication(fd);
      setDone(true);
      toast.success("Application received.");
      onDone?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="application-success" className="border border-border bg-secondary p-10 text-center">
        <CheckCircle2 size={40} className="mx-auto text-brand" />
        <h3 className="mt-6 font-display text-2xl tracking-tight">Application received.</h3>
        <p className="mt-3 text-sm text-muted-foreground">
          Thank you, {form.name.split(" ")[0]}. Our team will review your profile{job ? ` for ${job.title}` : ""} and be in touch.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`${tid}-form`} className="grid gap-4 sm:grid-cols-2">
      <input name="name" value={form.name} onChange={change} placeholder="Full name *" className={FIELD} data-testid={`${tid}-name`} />
      <input name="email" type="email" value={form.email} onChange={change} placeholder="Email *" className={FIELD} data-testid={`${tid}-email`} />
      <input name="phone" value={form.phone} onChange={change} placeholder="Phone" className={FIELD} data-testid={`${tid}-phone`} />
      <input name="location" value={form.location} onChange={change} placeholder="Location" className={FIELD} data-testid={`${tid}-location`} />
      {!job && (
        <input name="role" value={form.role} onChange={change} placeholder="Target role / discipline *" className={`${FIELD} sm:col-span-2`} data-testid={`${tid}-role`} />
      )}
      <input name="experience" value={form.experience} onChange={change} placeholder="Years of experience" className={`${FIELD} sm:col-span-2`} data-testid={`${tid}-experience`} />
      <textarea name="message" value={form.message} onChange={change} rows={4} placeholder="Brief note, clearance status (if applicable)" className={`${FIELD} sm:col-span-2`} data-testid={`${tid}-message`} />

      {questions.length > 0 && (
        <div className="space-y-4 border-t border-border pt-5 sm:col-span-2" data-testid={`${tid}-screening`}>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand">Screening questions</div>
          {questions.map((q, i) => (
            <div key={q.id || i}>
              <label className="mb-1.5 block text-sm text-navy">{q.question}{q.required && <span className="text-destructive"> *</span>}</label>
              {q.type === "yes_no" ? (
                <select value={answers[q.id] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))} className={FIELD} data-testid={`${tid}-q-${i}`}>
                  <option value="">Select…</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              ) : q.type === "long" ? (
                <textarea value={answers[q.id] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))} rows={3} className={FIELD} data-testid={`${tid}-q-${i}`} />
              ) : (
                <input value={answers[q.id] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))} className={FIELD} data-testid={`${tid}-q-${i}`} />
              )}
            </div>
          ))}
        </div>
      )}

      {job?.acknowledgment_required && (
        <label className="flex cursor-pointer items-start gap-3 border border-border bg-secondary p-4 text-sm sm:col-span-2" data-testid={`${tid}-ack-label`}>
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" data-testid={`${tid}-ack`} />
          <span className="text-muted-foreground">{ackText}</span>
        </label>
      )}

      <label className="group flex cursor-pointer items-center justify-between gap-3 border border-dashed border-border bg-secondary px-4 py-3 text-sm transition-colors hover:border-brand sm:col-span-2" data-testid={`${tid}-resume-label`}>
        <span className="flex items-center gap-3 text-muted-foreground">
          {file ? <FileText size={18} className="text-brand" /> : <Upload size={18} />}
          {file ? file.name : "Attach resume (PDF, DOC, DOCX · max 10MB) *"}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-brand">Browse</span>
        <input type="file" accept=".pdf,.doc,.docx" onChange={onFile} className="hidden" data-testid={`${tid}-resume-input`} />
      </label>

      <button type="submit" disabled={loading} data-testid={`${tid}-submit`} className="group mt-1 inline-flex items-center gap-2 bg-brand px-7 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-navy disabled:opacity-60 sm:col-span-2">
        {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
        {loading ? "Submitting" : job ? "Submit application" : "Join talent network"}
      </button>
    </form>
  );
}
